import * as React from 'react';
import propz from 'propz';
import { Loader } from '../../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../../components/SimpleModal/SimpleModal';
import { StatusModal } from '../../../../../components/StatusModal/StatusModal';
import { HelpModal } from '../../../../../components/HelpModal/HelpModal';
import { HelpIconButton } from '../../../../../components/HelpIconButton/HelpIconButton';
import { getAgesGroupFormatted } from '../../../../../helpers/accessor/accessor';
import {
  programmeBookingFormatCountdown,
  programmeBookingToMs,
  programmeBookingWeekdayOrderKey,
  programmeBookingFormatTimeRange,
  programmeBookingFormatDateRange,
  programmeBookingThrowIfOkError,
  programmeBookingFieldKey,
  programmeBookingIsFieldRequired,
  programmeBookingFieldType,
  programmeBookingValidateAnswers,
  programmeBookingBuildAnsweredFields,
  programmeBookingBuildInitialAnswersFromFields,
  programmeBookingHasAnyAnsweredValue,
  programmeBookingBuildAnswersFromInviteFields,
  programmeBookingGetInviteStatus,
  programmeBookingFindExistingDraftInSameDay,
  programmeBookingFindPriorityConflictInDay,
  programmeBookingPickAnyPlayerDetails,
  programmeBookingFormatError,
  programmeBookingStatusMeta,
  programmeBookingFormatClubAges,
  programmeBookingGetUsedPrioritiesInDay,
  programmeBookingGetNextAvailablePriorityInDay
} from '../../../../../helpers/clubProgramme/clubProgrammeBooking';
import { ProgrammeBookingDraftPayload, ProgrammeBookingConfirmPayload } from './ProgrammeBooking.types';
import { getProgrammeClubGenderLabel } from '../../../../../views/GenericView/AdminView/ClubProgrammes/helpers/clubProgrammeClubsContent';
import { ProgrammeBookingContentProps } from './ProgrammeBooking.contracts';
import { FormDefinition } from '../../../../../types/formSchema';
import { FormRendererV2 } from '../../../../../components/FormRendererV2/FormRendererV2';
import {
  FormAnswerMap,
  FormErrorMap,
  answerMapToArray,
  answersToMap,
  validateFormDefinition
} from '../../../../../helpers/form/formRuntime';
import {
  applyAnswersToLegacyFields,
  formDefinitionToLegacyFields
} from '../../../../../helpers/form/legacyFormAdapter';
import { getSubmissionElementIds, hasFormFlow } from '../../../../../helpers/form/formFlowRuntime';
import '../../../../../views/GenericView/ParentView/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.scss';

type DayRow = {
  day: string;
  dayLabel: string;
  clubs: any[];
};

type PresetScope = 'NONE' | 'DAY' | 'ALL';

interface PresetsState {
  scope: PresetScope;
  all: Record<string, any>;
  byDay: Record<string, Record<string, any>>;
  chosenAll?: boolean;
  chosenByDay?: Record<string, boolean>;
  dontAskAgain?: boolean;
}

export const ProgrammeBookingContent: React.FC<ProgrammeBookingContentProps> = ({
  user,
  history,
  actorLabel,
  actorFallbackLabel,
  data,
  isLoading,
  error,
  onReload,
  onDraftClub,
  onUndraftClub,
  onUpdateAcceptedClub,
  onCancelAcceptedClub,
  onConfirmProgramme
}) => {
  const [activeDay, setActiveDay] = React.useState<string>('MON');
  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [activeRow, setActiveRow] = React.useState<any>(null);
  const [answers, setAnswers] = React.useState<Record<string, any>>({});
  const [formAnswers, setFormAnswers] = React.useState<FormAnswerMap>({});
  const [formErrors, setFormErrors] = React.useState<FormErrorMap>({});
  const [isFormFlowComplete, setIsFormFlowComplete] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [isReplaceModalOpen, setIsReplaceModalOpen] = React.useState(false);
  const [replaceFromRow, setReplaceFromRow] = React.useState<any>(null);
  const [replaceToRow, setReplaceToRow] = React.useState<any>(null);

  const [draftPriority, setDraftPriority] = React.useState<number | null>(null);

  const [isPriorityModalOpen, setIsPriorityModalOpen] = React.useState(false);
  const [priorityRow, setPriorityRow] = React.useState<any>(null);
  const [priorityValue, setPriorityValue] = React.useState<number | null>(null);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isSubmitStatusModalOpen, setIsSubmitStatusModalOpen] = React.useState(false);

  const [, setAcceptedActionMode] = React.useState<'CHANGE' | 'REMOVE'>('CHANGE');
  const [isRemoveAcceptedModalOpen, setIsRemoveAcceptedModalOpen] = React.useState(false);
  const [removeAcceptedRow, setRemoveAcceptedRow] = React.useState<any>(null);

  const [presets, setPresets] = React.useState<PresetsState>({
    scope: 'NONE',
    all: {},
    byDay: {},
    chosenAll: false,
    chosenByDay: {}
  });

  const [isPresetPromptOpen, setIsPresetPromptOpen] = React.useState(false);
  const [presetPromptPayload, setPresetPromptPayload] = React.useState<{
    dayKey: string;
    fields: any[];
    answers: Record<string, any>;
  } | null>(null);

  const [isHowItWorksOpen, setIsHowItWorksOpen] = React.useState<boolean>(false);

  const [isLeaveWarningOpen, setIsLeaveWarningOpen] = React.useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = React.useState<null | (() => void)>(null);

  React.useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  React.useEffect(() => {
    const days: DayRow[] = (propz.get(data || {}, ['days'], []) || []) as any;
    const stillExists = days.some(d => String(propz.get(d, ['day'], '')) === String(activeDay));

    if (!stillExists) {
      const firstNonEmpty = days.find(d => (propz.get(d, ['clubs'], []) || []).length > 0);
      const nextDay = String(propz.get(firstNonEmpty, ['day'], 'MON')) || 'MON';
      if (nextDay !== activeDay) {
        setActiveDay(nextDay);
      }
    }
  }, [data, activeDay]);

  const removeDraft = React.useCallback(
    async (row: any) => {
      try {
        setSaveError(null);
        setIsSaving(true);

        const playerDetails = propz.get(row, ['invite', 'playerDetails'], null);

        const payload: ProgrammeBookingConfirmPayload = {
          playerDetails: {
            userId: String(propz.get(playerDetails, ['userId'], '')),
            permissionId: String(propz.get(playerDetails, ['permissionId'], ''))
          }
        };

        await onUndraftClub(row, payload);
        await onReload();
      } catch (e) {
        const msg =
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Failed to remove draft'))
            )
          ) || 'Failed to remove draft';

        setSaveError(msg);
      } finally {
        setIsSaving(false);
      }
    },
    [onUndraftClub, onReload]
  );

  const cancelAccepted = React.useCallback(
    async (row: any) => {
      try {
        setSaveError(null);
        setIsSaving(true);

        const playerDetails = propz.get(row, ['invite', 'playerDetails'], null);

        const payload: ProgrammeBookingConfirmPayload = {
          playerDetails: {
            userId: String(propz.get(playerDetails, ['userId'], '')),
            permissionId: String(propz.get(playerDetails, ['permissionId'], ''))
          }
        };

        await onCancelAcceptedClub(row, payload);
        await onReload();
      } catch (e) {
        const msg =
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Failed to cancel selection'))
            )
          ) || 'Failed to cancel selection';

        setSaveError(msg);
      } finally {
        setIsSaving(false);
      }
    },
    [onCancelAcceptedClub, onReload]
  );

  const normalizeDayKey = (v: any) => {
    const s = String(v || '')
      .trim()
      .toUpperCase();
    if (!s) return '';
    return s.length >= 3 ? s.slice(0, 3) : s;
  };

  const normalizePresetText = (v: any) =>
    String(v || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const getPresetKey = (f: any) => {
    const heading = normalizePresetText(propz.get(f, ['heading'], ''));
    const type = String(programmeBookingFieldType(f) || '').toUpperCase();
    const opts = (propz.get(f, ['enumOptions'], []) || []).map(normalizePresetText).join('|');
    return `${type}::${heading}::${opts}`;
  };

  const applyAnswersToPresets = (scope: PresetScope, dayKey: string, fields: any[], ans: Record<string, any>) => {
    const dk = normalizeDayKey(dayKey);

    const patch: Record<string, any> = {};
    fields.forEach(f => {
      const fieldKey = programmeBookingFieldKey(f);
      const presetKey = getPresetKey(f);

      const v = ans[fieldKey];
      if (typeof v === 'undefined' || v === '') return;

      patch[presetKey] = v;
    });

    if (!Object.keys(patch).length) return;

    setPresets(prev => {
      if (scope === 'ALL') {
        return { ...prev, scope, all: { ...prev.all, ...patch } };
      }
      if (scope === 'DAY') {
        return {
          ...prev,
          scope,
          byDay: {
            ...prev.byDay,
            [dk]: { ...(prev.byDay[dk] || {}), ...patch }
          }
        };
      }
      return { ...prev, scope: 'NONE' };
    });
  };

  const prefillAnswersForClub = (dayKey: string, fields: any[], base: Record<string, any>) => {
    const dk = normalizeDayKey(dayKey);

    const srcAll = presets.all || {};
    const srcDay = presets.byDay && presets.byDay[dk] ? presets.byDay[dk] : {};
    const src = presets.scope === 'DAY' ? srcDay : presets.scope === 'ALL' ? srcAll : {};

    if (!src || !Object.keys(src).length) return base;

    const out = { ...base };

    fields.forEach(f => {
      const fieldKey = programmeBookingFieldKey(f);
      const presetKey = getPresetKey(f);

      const cur = out[fieldKey];
      const hasMeaningfulValue =
        typeof cur !== 'undefined' && cur !== null && !(typeof cur === 'string' && cur.trim() === '');

      if (hasMeaningfulValue) return;
      if (typeof src[presetKey] === 'undefined') return;

      out[fieldKey] = src[presetKey];
    });

    return out;
  };

  const getRowDayKey = (row: any) => {
    const fromRow = propz.get(row, ['day'], propz.get(row, ['_day'], undefined));
    return normalizeDayKey(fromRow || activeDay);
  };

  const getFormDefinition = (row: any): FormDefinition | null => {
    const invite = propz.get(row, ['invite'], {});
    return Number(propz.get(invite, ['formSchemaVersion'], 0)) === 2 &&
      propz.get(invite, ['formDefinition'], null)
      ? (propz.get(invite, ['formDefinition']) as FormDefinition)
      : null;
  };

  const setV2AnswersFromRow = (row: any) => {
    setFormAnswers(answersToMap(propz.get(row, ['invite', 'formAnswers'], []) || []));
    setFormErrors({});
    setIsFormFlowComplete(false);
  };

  const clearModalAnswers = () => {
    setAnswers({});
    setFormAnswers({});
    setFormErrors({});
    setIsFormFlowComplete(false);
  };

  const getDayRowByRow = (row: any) => {
    const dayKey = getRowDayKey(row);
    return (days || []).find(d => String(propz.get(d, ['day'], '')) === String(dayKey)) || null;
  };

  const getPriorityDefaultsForRow = (row: any) => {
    const min = Number(propz.get(data, ['selectionRules', 'priorityRange', 'min'], 1));
    const max = Number(propz.get(data, ['selectionRules', 'priorityRange', 'max'], 10));
    const existing = propz.get(row, ['invite', 'priority'], undefined);
    const existingNumber = typeof existing === 'number' ? existing : parseInt(String(existing), 10);

    const dayRow = getDayRowByRow(row);
    const clubId = String(propz.get(row, ['clubId'], ''));

    const usedPriorities = dayRow ? programmeBookingGetUsedPrioritiesInDay(dayRow, clubId) : [];
    const nextAvailablePriority = dayRow
      ? programmeBookingGetNextAvailablePriorityInDay(dayRow, min, max, clubId)
      : min;

    return {
      min,
      max,
      usedPriorities,
      nextAvailablePriority,
      initialPriority: Number.isFinite(existingNumber)
        ? existingNumber
        : nextAvailablePriority !== null
        ? nextAvailablePriority
        : min
    };
  };

  const safeData: any = data || {};
  const days: DayRow[] = (propz.get(safeData, ['days'], []) || []) as any;
  const active = days.find(d => String(d.day) === String(activeDay)) || days[0];

  const activeClubsAll = (propz.get(active, ['clubs'], []) || []) as any[];

  const replaceDayLabel =
    String(propz.get(active, ['dayLabel'], '')).trim() || String(propz.get(active, ['day'], '')).trim() || 'day';

  const programmeName = safeData ? propz.get(safeData, ['programme', 'name'], 'Programme') : 'Programme';
  const programmeDescription = safeData
    ? String(propz.get(safeData, ['programme', 'description'], '') || '').trim()
    : '';

  const opensAt = safeData ? propz.get(safeData, ['booking', 'opensAt'], null) : null;
  const closesAt = safeData ? propz.get(safeData, ['booking', 'closesAt'], null) : null;

  const bookingCloseText = closesAt ? new Date(closesAt).toLocaleString() : 'the booking deadline';

  const opensAtMs = programmeBookingToMs(opensAt);
  const closesAtMs = programmeBookingToMs(closesAt);

  const secToOpen = opensAtMs && nowMs < opensAtMs ? Math.ceil((opensAtMs - nowMs) / 1000) : null;

  const secToClose = closesAtMs && nowMs < closesAtMs ? Math.ceil((closesAtMs - nowMs) / 1000) : null;

  const isBeforeOpen = secToOpen !== null;
  const isAfterClose = closesAtMs ? nowMs >= closesAtMs : false;

  const preAccess = safeData
    ? String(propz.get(safeData, ['booking', 'preBookingAccess'], '') || '').toUpperCase()
    : '';

  const canSee = safeData ? !!propz.get(safeData, ['visibility', 'canSeeInvites'], false) : false;
  const canDraft = safeData ? !!propz.get(safeData, ['visibility', 'canDraft'], false) : false;
  const canConfirm = safeData ? !!propz.get(safeData, ['visibility', 'canConfirm'], false) : false;

  const cntPending = safeData ? Number(propz.get(safeData, ['counts', 'pending'], 0) || 0) : 0;
  const cntDraft = safeData ? Number(propz.get(safeData, ['counts', 'draft'], 0) || 0) : 0;
  const cntAccepted = safeData ? Number(propz.get(safeData, ['counts', 'accepted'], 0) || 0) : 0;

  const isHideBeforeOpen = isBeforeOpen && preAccess === 'HIDE';
  const showTopStats = !isBeforeOpen || preAccess === 'DRAFT';
  const showLockedPreOpenState = isHideBeforeOpen;

  const selectionMode = safeData
    ? String(propz.get(safeData, ['selectionRules', 'mode'], 'SINGLE')).toUpperCase()
    : 'SINGLE';

  const priorityRangeMax = Number(propz.get(safeData, ['selectionRules', 'priorityRange', 'max'], 0) || 0);

  const priorityInfoMessage =
    selectionMode === 'PRIORITY' && priorityRangeMax > 0
      ? `You can select up to ${priorityRangeMax} club${
          priorityRangeMax === 1 ? '' : 's'
        } per day. Use priority 1 for the club you most want, then rank your other choices in order of preference.`
      : '';

  const helpSummary =
    selectionMode === 'SINGLE' ? 'One club per day' : 'Multiple clubs per day with different priorities';

  const helpCurrentState = isAfterClose
    ? 'Booking is closed.'
    : isBeforeOpen
    ? preAccess === 'HIDE'
      ? opensAt
        ? `Booking has not opened yet. Clubs will become available when booking opens on ${new Date(
            opensAt
          ).toLocaleString()}.`
        : 'Booking has not opened yet.'
      : preAccess === 'VIEW'
      ? opensAt
        ? `Booking has not opened yet. You can review clubs now, and booking will open on ${new Date(
            opensAt
          ).toLocaleString()}.`
        : 'Booking has not opened yet. You can review clubs now.'
      : opensAt
      ? `Booking has not opened yet. You can prepare draft selections now, and send them after booking opens on ${new Date(
          opensAt
        ).toLocaleString()}.`
      : 'Booking has not opened yet. You can prepare draft selections now.'
    : closesAt
    ? `Booking is open now. Complete and send your selections before ${new Date(closesAt).toLocaleString()}.`
    : 'Booking is open now.';

  const helpInvitesOnlyText =
    'If you see “Invites only”, this means the club is restricted to invited children, and this child has been invited to book it.';

  const helpDraftSentText =
    'Draft selections are saved but not sent to school until you use Review & send. Accepted or sent selections are the ones already submitted to school.';

  const windowBits = [
    opensAt ? `Opens: ${new Date(opensAt).toLocaleString()}` : undefined,
    closesAt ? `Closes: ${new Date(closesAt).toLocaleString()}` : undefined
  ].filter(Boolean);

  const allRows = (days || []).reduce((acc: any[], d: any) => {
    const clubs = (propz.get(d, ['clubs'], []) || []) as any[];

    const mapped = clubs.map((r: any) => ({
      ...r,
      _day: String(propz.get(d, ['day'], propz.get(r, ['day'], ''))),
      _dayLabel: String(propz.get(d, ['dayLabel'], propz.get(r, ['dayLabel'], propz.get(r, ['day'], ''))))
    }));

    return acc.concat(mapped);
  }, []);

  const isPendingSubmitStatus = (status: any) => {
    const s = String(status || '').toUpperCase();

    return s === 'PROGRAMME_DRAFT' || s === 'PROGRAMME_DRAFT_UPDATE' || s === 'PROGRAMME_DRAFT_REMOVE';
  };

  const draftRows = allRows
    .filter(r => isPendingSubmitStatus(programmeBookingGetInviteStatus(r)))
    .sort((a, b) => programmeBookingWeekdayOrderKey(a._day) - programmeBookingWeekdayOrderKey(b._day));

  const acceptedRows = allRows
    .filter(r => programmeBookingGetInviteStatus(r) === 'ACCEPTED')
    .sort((a, b) => programmeBookingWeekdayOrderKey(a._day) - programmeBookingWeekdayOrderKey(b._day));

  const pendingSubmitRowsCount = draftRows.length;

  const hasUnsentSelections = pendingSubmitRowsCount > 0 && !isAfterClose;

  const attemptLeave = React.useCallback(
    (leaveAction: () => void) => {
      if (hasUnsentSelections) {
        setPendingLeaveAction(() => leaveAction);
        setIsLeaveWarningOpen(true);
        return;
      }

      leaveAction();
    },
    [hasUnsentSelections]
  );

  React.useEffect(() => {
    if (!hasUnsentSelections) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsentSelections]);

  const allowChangesAfterSubmit = Boolean(propz.get(safeData, ['booking', 'allowChangesAfterSubmit'], false));

  const hasSubmittedSelections = acceptedRows.length > 0;

  const isSubmittedLockedView = hasSubmittedSelections && !allowChangesAfterSubmit;

  const activeClubsVisible = isSubmittedLockedView
    ? activeClubsAll.filter((row: any) => {
        const status = String(propz.get(row, ['invite', 'invitationStatus'], '') || '').toUpperCase();
        return status === 'ACCEPTED';
      })
    : activeClubsAll;

  const helpCanDoNow = isSubmittedLockedView
    ? 'Your selections have already been sent to school and are now locked. You can review them here while waiting for allocation.'
    : isAfterClose
    ? 'You can review previous selections, but you cannot make new changes.'
    : isBeforeOpen
    ? preAccess === 'HIDE'
      ? 'Right now you cannot view clubs or make selections from the normal booking flow.'
      : preAccess === 'VIEW'
      ? 'Right now you can view clubs, but you cannot draft or send selections yet.'
      : 'Right now you can view clubs and prepare draft selections, but you cannot send them yet.'
    : canConfirm
    ? 'Right now you can select clubs, adjust your choices, and send them to school.'
    : canDraft
    ? 'Right now you can work on your selections.'
    : 'Right now booking actions are limited.';

  const groupRowsByDay = (rowsIn: any[]) => {
    const map = new Map<string, any[]>();

    (rowsIn || []).forEach(r => {
      const key = String(r._day || r.day || '').toUpperCase() || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });

    const orderedDays = Array.from(map.keys()).sort(
      (a, b) => programmeBookingWeekdayOrderKey(a) - programmeBookingWeekdayOrderKey(b)
    );

    return orderedDays.map(day => {
      const items = map.get(day) || [];

      const sorted = items.slice().sort((a, b) => {
        if (selectionMode === 'PRIORITY') {
          const pa = propz.get(a, ['invite', 'priority'], undefined);
          const pb = propz.get(b, ['invite', 'priority'], undefined);
          const na = typeof pa === 'number' ? pa : parseInt(String(pa), 10);
          const nb = typeof pb === 'number' ? pb : parseInt(String(pb), 10);
          const aa = Number.isFinite(na) ? na : 999;
          const bb = Number.isFinite(nb) ? nb : 999;
          if (aa !== bb) return aa - bb;
        }

        const an = String(propz.get(a, ['club', 'name'], '')).toLowerCase();
        const bn = String(propz.get(b, ['club', 'name'], '')).toLowerCase();
        return an.localeCompare(bn);
      });

      const dayLabel = String(items[0]?._dayLabel || items[0]?._day || day);

      return { day, dayLabel, rows: sorted };
    });
  };

  const draftByDay = groupRowsByDay(draftRows);
  const acceptedByDay = groupRowsByDay(acceptedRows);

  const reviewWarnings = React.useMemo(() => {
    const out: Array<{
      day: string;
      dayLabel: string;
      type: 'INVITES_ONLY_SKIPPED' | 'DAY_WITHOUT_SELECTION';
      message: string;
      clubs?: any[];
    }> = [];

    const selectedByDay = new Map<string, any[]>();

    [...draftRows, ...acceptedRows].forEach(row => {
      const dayKey = String(row._day || row.day || '').toUpperCase();
      if (!dayKey) return;

      if (!selectedByDay.has(dayKey)) selectedByDay.set(dayKey, []);
      selectedByDay.get(dayKey)!.push(row);
    });

    (days || []).forEach((dayRow: any) => {
      const dayKey = String(propz.get(dayRow, ['day'], '') || '').toUpperCase();
      const dayLabel = String(propz.get(dayRow, ['dayLabel'], dayKey) || dayKey);
      const clubs = (propz.get(dayRow, ['clubs'], []) || []) as any[];

      if (!clubs.length) return;

      const selected = selectedByDay.get(dayKey) || [];
      const invitesOnlyClubs = clubs.filter(c => !!propz.get(c, ['invitesOnly'], false));

      if (selected.length === 0) {
        out.push({
          day: dayKey,
          dayLabel,
          type: 'DAY_WITHOUT_SELECTION',
          message: `No club selected for ${dayLabel}, although there are clubs available on this day.`,
          clubs
        });
        return;
      }

      if (!invitesOnlyClubs.length) return;

      if (selectionMode === 'SINGLE') {
        const selectedClubIds = new Set(selected.map(r => String(propz.get(r, ['clubId'], ''))));
        const skippedInvitesOnly = invitesOnlyClubs.filter(
          c => !selectedClubIds.has(String(propz.get(c, ['clubId'], '')))
        );

        if (skippedInvitesOnly.length > 0) {
          const actorWasWere = actorLabel ? 'this child was' : 'you were';

          out.push({
            day: dayKey,
            dayLabel,
            type: 'INVITES_ONLY_SKIPPED',
            message:
              skippedInvitesOnly.length === 1
                ? `On ${dayLabel}, ${actorWasWere} specially invited to "${String(
                    propz.get(skippedInvitesOnly[0], ['club', 'name'], 'this club')
                  )}", but another club has been selected instead.`
                : `On ${dayLabel}, ${actorWasWere} specially invited to ${skippedInvitesOnly.length} invite-only clubs, but another club has been selected instead.`,
            clubs: skippedInvitesOnly
          });
        }
      }

      if (selectionMode === 'PRIORITY') {
        const selectedWithPriority = selected
          .map(r => ({
            row: r,
            priority: Number(propz.get(r, ['invite', 'priority'], NaN))
          }))
          .filter(x => Number.isFinite(x.priority));

        invitesOnlyClubs.forEach(inviteClub => {
          const inviteClubId = String(propz.get(inviteClub, ['clubId'], ''));
          const selectedInvite = selectedWithPriority.find(
            x => String(propz.get(x.row, ['clubId'], '')) === inviteClubId
          );

          if (!selectedInvite && selectedWithPriority.length > 0) {
            const betterChoices = selectedWithPriority.slice().sort((a, b) => a.priority - b.priority);

            if (betterChoices.length > 0) {
              const actorPhrase = actorLabel ? 'this child was' : 'you were';

              out.push({
                day: dayKey,
                dayLabel,
                type: 'INVITES_ONLY_SKIPPED',
                message:
                  `On ${dayLabel}, ${actorPhrase} specially invited to "${String(
                    propz.get(inviteClub, ['club', 'name'], 'this club')
                  )}", ` + `but it has not been selected, while other higher-priority choices were made for that day.`,
                clubs: [inviteClub]
              });
            }
            return;
          }

          if (selectedInvite) {
            const higherPriorityChoices = selectedWithPriority.filter(x => x.priority < selectedInvite.priority);

            if (higherPriorityChoices.length > 0) {
              const actorPhrase = actorLabel ? 'this child was' : 'you were';
              out.push({
                day: dayKey,
                dayLabel,
                type: 'INVITES_ONLY_SKIPPED',
                message:
                  `On ${dayLabel}, ${actorPhrase} specially invited to "${String(
                    propz.get(inviteClub, ['club', 'name'], 'this club')
                  )}", ` + `but another club has a higher priority.`,
                clubs: [inviteClub]
              });
            }
          }
        });
      }
    });

    return out;
  }, [days, draftRows, acceptedRows, selectionMode]);

  const renderReviewClubBadges = (row: any) => {
    const out: React.ReactNode[] = [];

    if (propz.get(row, ['invitesOnly'], false)) {
      out.push(
        <span key="invites-only" className="badge badge-dark mr-2">
          Invites only
        </span>
      );
    }

    if (selectionMode === 'PRIORITY') {
      const pr = propz.get(row, ['invite', 'priority'], undefined);
      if (typeof pr === 'number') {
        out.push(
          <span key="priority" className="badge badge-info mr-2">
            P{pr}
          </span>
        );
      }
    }

    return out.length ? <div className="mt-1">{out}</div> : null;
  };

  if (isLoading) return <Loader />;

  return (
    <div className="mt-3 container-fluid programmeBooking">
      <div className="programmeBookingHero mb-3">
        <div className="programmeBookingHeroTop">
          <div className="programmeBookingHeroMain">
            <div className="programmeBookingBreadcrumb">
              <button type="button" onClick={() => attemptLeave(() => history.push('/clubProgrammes'))}>← Back to clubs</button>
              <span aria-hidden="true" />
              <strong>Programme booking</strong>
            </div>

            <div className="programmeBookingTitleLine">
              <span className="programmeBookingProgrammeName">{programmeName}</span>

              {actorLabel ? <span className="programmeBookingChildPill">Booking for: {actorLabel}</span> : null}
            </div>

            <div className="programmeBookingHeaderChips">
              <span><strong>{cntPending}</strong> available</span>
              <span><strong>{cntDraft}</strong> draft</span>
              <span className="is-success"><strong>{cntAccepted}</strong> submitted</span>
            </div>

            {programmeDescription ? (
              <div className="programmeBookingProgrammeDescription">{programmeDescription}</div>
            ) : null}
          </div>
          <div className="programmeBookingHeroActions">
            <div className="programmeBookingActionRow programmeBookingActionRowTight">
              {!isSubmittedLockedView ? (
                <button
                  className="btn btn-primary programmeBookingPrimaryAction"
                  disabled={!canConfirm || pendingSubmitRowsCount <= 0 || isSaving || isConfirming}
                  onClick={() => {
                    setConfirmError(null);
                    setIsConfirmModalOpen(true);
                  }}
                >
                  Review &amp; send
                </button>
              ) : null}
            </div>

            {!showLockedPreOpenState ? (
              <div className="programmeBookingActionHint">
                {isSubmittedLockedView
                  ? 'Selections already sent to school.'
                  : pendingSubmitRowsCount > 0
                  ? 'You have unsent booking changes. Review and send to submit them to school.'
                  : 'No unsent booking changes.'}
              </div>
            ) : null}
          </div>
        </div>

        <div className="programmeBookingInfoToggle programmeBookingInfoToggleCompact">
          <HelpIconButton
            onClick={() => setIsHowItWorksOpen(true)}
            label={`How booking works: ${String(programmeName || 'Programme')}`}
          />
          <div className="programmeBookingInfoToggleMain">
            <div className="programmeBookingInfoTitle programmeBookingInfoTitleCompact">
              <span>How booking works</span>
            </div>

            <div className="programmeBookingInfoSummary programmeBookingInfoSummaryCompact">
              {helpSummary}
              {priorityInfoMessage ? ` • ${priorityInfoMessage}` : ''}
              {isBeforeOpen && opensAt ? ` • Opens on ${new Date(opensAt).toLocaleString()}` : ''}
              {!isBeforeOpen && !isAfterClose && closesAt
                ? ` • Confirm before ${new Date(closesAt).toLocaleString()}`
                : ''}
              {isAfterClose ? ' • Booking closed' : ''}
            </div>
          </div>
        </div>

        <HelpModal
          isOpen={isHowItWorksOpen}
          title={`How booking works: ${String(programmeName || 'Programme')}`}
          badgeText={selectionMode}
          subtitle="Booking guidance for the current programme and its current booking state."
          onClose={() => setIsHowItWorksOpen(false)}
        >
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Selection mode</div>
                <div className="text-muted">
                  {selectionMode === 'SINGLE' ? (
                    <>
                      You can choose <b>one club per day</b>. If you pick another club on the same day, it replaces your
                      previous draft selection.
                    </>
                  ) : (
                    <>
                      You can choose <b>multiple clubs per day</b>, but each selected club must have a{' '}
                      <b>different priority</b>. Priority <b>1</b> is the highest.
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Current booking state</div>
                <div className="text-muted">{helpCurrentState}</div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">What you can do right now</div>
                <div className="text-muted">{helpCanDoNow}</div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Invites only clubs</div>
                <div className="text-muted">{helpInvitesOnlyText}</div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Draft and sent selections</div>
                <div className="text-muted">{helpDraftSentText}</div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Booking window</div>
                <div className="text-muted">
                  {opensAt ? (
                    <div>
                      Opens: <b>{new Date(opensAt).toLocaleString()}</b>
                    </div>
                  ) : null}
                  {closesAt ? (
                    <div>
                      Closes: <b>{new Date(closesAt).toLocaleString()}</b>
                    </div>
                  ) : null}
                  {!opensAt && !closesAt ? 'Booking window is not configured.' : null}
                </div>
              </div>
            </div>
          </div>
        </HelpModal>

        <div className="programmeBookingStatusRow mt-2">
          <div className="programmeBookingStatusMeta">
            <div className="programmeBookingWindowText">
              {windowBits.length ? windowBits.join(' • ') : 'Booking window not configured'}
            </div>

            {showTopStats ? (
              <div className="programmeBookingStats">
                <span className="programmeBookingStat programmeBookingStatPending">
                  Available for booking {cntPending}
                </span>
                <span className="programmeBookingStat programmeBookingStatDraft">Draft {cntDraft}</span>
                <span className="programmeBookingStat programmeBookingStatAccepted">
                  Submitted to school {cntAccepted}
                </span>
              </div>
            ) : null}
          </div>

          {isAfterClose ? (
            <div className="programmeBookingStatus programmeBookingStatusClosed">Booking is closed.</div>
          ) : isBeforeOpen ? (
            <div className="programmeBookingStatus programmeBookingStatusPending">
              <span>Booking opens in</span>
              <b style={{ marginLeft: 4 }}>{programmeBookingFormatCountdown(secToOpen!)}</b>
              {opensAt ? <span className="ml-2">(opens on {new Date(opensAt).toLocaleString()})</span> : null}
            </div>
          ) : (
            <div className="programmeBookingStatus programmeBookingStatusOpen">
              <span>Booking is open</span>
              {secToClose !== null ? (
                <>
                  <span style={{ marginLeft: 8 }}>• closes in</span>
                  <b style={{ marginLeft: 4 }}>{programmeBookingFormatCountdown(secToClose)}</b>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <StatusModal
        isOpen={isSubmitStatusModalOpen}
        status="success"
        title="Selections sent to school"
        message={
          allowChangesAfterSubmit
            ? 'Your selections have been submitted successfully. The school will now review them and complete allocations.'
            : 'Your selections have been submitted successfully and are now locked while the school reviews allocations.'
        }
        lines={[
          'Submitting selections does not guarantee a confirmed place yet.',
          'Once the school completes allocations, you will receive a notification.',
          'Allocated clubs will then appear in the calendar.'
        ]}
        onClose={() => setIsSubmitStatusModalOpen(false)}
      />

      {!data ? (
        <div className="alert alert-light border">No data</div>
      ) : showLockedPreOpenState ? (
        <div
          className="alert border"
          style={{
            background: '#fffdf7',
            borderColor: '#f6e7b8',
            color: '#6b5a1f'
          }}
        >
          <div className="font-weight-bold mb-1">Booking has not opened yet.</div>
          <div>
            You will be able to view and book clubs once booking opens
            {opensAt ? (
              <>
                {' '}
                on <b>{new Date(opensAt).toLocaleString()}</b>
              </>
            ) : null}
            .
          </div>
        </div>
      ) : !canSee ? (
        <div className="alert alert-light border">This programme is not available right now.</div>
      ) : (
        <>
          {!canDraft && isBeforeOpen && (
            <div className="alert alert-info">
              You can view the available clubs, but booking is not open yet.
              {opensAt ? (
                <span className="ml-2">
                  Opens on <b>{new Date(opensAt).toLocaleString()}</b>.
                </span>
              ) : null}
            </div>
          )}

          {isSubmittedLockedView ? (
            <div
              className="alert border mb-4"
              style={{
                background: '#f5fbf7',
                borderColor: '#bfe6cc',
                color: '#1f5132'
              }}
            >
              <div className="font-weight-bold mb-1">Your selections have been sent to school.</div>
              <div>
                They are now locked for changes. Once the school completes allocations, you will receive a notification
                and your allocated clubs will appear in your calendar.
              </div>
            </div>
          ) : null}

          <div className="programmeBookingTabs mb-4" role="tablist" aria-label="Weekday tabs">
            {days.map(d => {
              const day = String(d.day);
              const label = String(d.dayLabel || d.day);
              const count = (propz.get(d, ['clubs'], []) || []).length;
              const isActive = day === String(activeDay);

              return (
                <button
                  key={day}
                  type="button"
                  className={`programmeBookingDayTab ${isActive ? 'programmeBookingDayTabActive' : ''} ${
                    !count ? 'programmeBookingDayTabDim' : ''
                  }`}
                  onClick={() => setActiveDay(day)}
                >
                  <span className="programmeBookingDayTabLabel">{label}</span>
                  <span className="programmeBookingDayTabCount">{count}</span>
                </button>
              );
            })}
          </div>

          {!active ? (
            <div className="alert alert-light border">No days</div>
          ) : (
            <>
              {!activeClubsVisible.length ? (
                <div className="alert alert-light border">
                  <b>{String(active.dayLabel || active.day)}</b>: no clubs.
                  <div className="small text-muted mt-1">Try another day tab above.</div>
                </div>
              ) : (
                <div className="row">
                  {activeClubsVisible.map((row: any) => {
                    const club = propz.get(row, ['club'], {}) || {};
                    const clubId = String(propz.get(row, ['clubId'], ''));
                    const clubName = String(propz.get(club, ['name'], 'Club'));
                    const schedule = propz.get(club, ['schedule'], {});
                    const time = programmeBookingFormatTimeRange(schedule);
                    const dates = programmeBookingFormatDateRange(schedule);

                    const stRaw = propz.get(row, ['invite', 'invitationStatus'], '');
                    const stBase = programmeBookingStatusMeta(String(stRaw));
                    const invStatusUpper = String(stRaw || '').toUpperCase();

                    const st =
                      invStatusUpper === 'PENDING' && isBeforeOpen && preAccess === 'VIEW'
                        ? { text: 'Preview only', cls: 'badge badge-secondary' }
                        : invStatusUpper === 'PENDING' && isBeforeOpen && preAccess === 'DRAFT'
                        ? { text: 'Available to pre-select', cls: 'badge badge-info' }
                        : stBase;

                    const invStatus = String(propz.get(row, ['invite', 'invitationStatus'], '') || '').toUpperCase();

                    const isDraft = isPendingSubmitStatus(invStatus);
                    const isAccepted = invStatus === 'ACCEPTED';

                    const canInteract = !!propz.get(row, ['actions', 'canDraft'], canDraft);

                    const canChangeAccepted = isAccepted ? allowChangesAfterSubmit && !!canDraft : false;
                    const canClickButton = isAccepted ? canChangeAccepted : canInteract;

                    const fields = (propz.get(row, ['invite', 'fields'], []) || []) as any[];
                    const isAnswered = programmeBookingHasAnyAnsweredValue(fields);

                    const gender = getProgrammeClubGenderLabel(club);
                    const maxParticipants = propz.get(club, ['maxParticipants'], undefined);
                    const sportName = String(propz.get(club, ['sport', 'name'], '') || '').trim();

                    return (
                      <div className="col-12 col-md-6 col-lg-4 mb-3" key={clubId || clubName}>
                        <div
                          className={[
                            'card',
                            'h-100',
                            'programmeBookingCard',
                            isAccepted ? 'programmeBookingCardAccepted' : '',
                            isDraft ? 'programmeBookingCardSelected' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <div className="card-body d-flex flex-column">
                            <div className="programmeBookingCardHeader">
                              <div className="d-flex align-items-start flex-grow-1">
                                {isDraft || isAccepted ? (
                                  <div
                                    className={[
                                      'programmeBookingLeftBar',
                                      isAccepted ? 'programmeBookingLeftBarAccepted' : 'programmeBookingLeftBarDraft'
                                    ].join(' ')}
                                  />
                                ) : null}

                                <div className="programmeBookingCardTitleWrap">
                                  <div className="programmeBookingCardTitleRow">
                                    <h5 className="programmeBookingClubTitle">{clubName}</h5>
                                    <span className={`${st.cls} programmeBookingCardStatusBadge`}>{st.text}</span>
                                  </div>

                                  <div className="programmeBookingCardMeta">
                                    <div className="programmeBookingCardMetaRow">
                                      <span className="programmeBookingCardMetaItem">
                                        <span className="programmeBookingCardMetaLabel">Time</span>
                                        <span className="programmeBookingCardMetaValue">{time || '—'}</span>
                                      </span>

                                      {dates ? (
                                        <span className="programmeBookingCardMetaItem">
                                          <span className="programmeBookingCardMetaLabel">Dates</span>
                                          <span className="programmeBookingCardMetaValue">{dates}</span>
                                        </span>
                                      ) : null}
                                    </div>

                                    <div className="programmeBookingCardMetaRow">
                                      {gender ? (
                                        <span className="programmeBookingCardMetaItem">
                                          <span className="programmeBookingCardMetaLabel">Audience</span>
                                          <span className="programmeBookingCardMetaValue">{String(gender)}</span>
                                        </span>
                                      ) : null}

                                      <span className="programmeBookingCardMetaItem">
                                        <span className="programmeBookingCardMetaLabel">Ages</span>
                                        <span className="programmeBookingCardMetaValue">
                                          {programmeBookingFormatClubAges(club, user, getAgesGroupFormatted)}
                                        </span>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mb-3">
                              <div className="d-flex flex-wrap mb-1">
                                {sportName ? (
                                  <span className="badge badge-light border mr-2 mb-2" title="Sport">
                                    {sportName}
                                  </span>
                                ) : null}

                                {typeof maxParticipants !== 'undefined' ? (
                                  <span className="badge badge-light border mr-2 mb-2">
                                    Max: {Number(maxParticipants) === 0 ? 'Unlimited' : String(maxParticipants)}
                                  </span>
                                ) : null}
                              </div>

                              <div className="d-flex flex-wrap">
                                {propz.get(row, ['invitesOnly'], false) ? (
                                  <>
                                    <span
                                      className="badge badge-dark mr-2 mb-2"
                                      title={
                                        actorLabel
                                          ? 'This club can be booked only by invited children.'
                                          : 'This club can be booked only by invited students.'
                                      }
                                    >
                                      Invites only
                                    </span>
                                    <span
                                      className="badge badge-success mr-2 mb-2"
                                      title={
                                        actorLabel
                                          ? 'Good news: this child is invited to this club.'
                                          : 'Good news: you are invited to this club.'
                                      }
                                    >
                                      Invited
                                    </span>
                                  </>
                                ) : null}

                                {isAnswered ? (
                                  <span
                                    role="button"
                                    className="badge badge-primary mr-2 mb-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSaveError(null);
                                      setActiveRow(row);

                                      const fs = (propz.get(row, ['invite', 'fields'], []) || []) as any[];
                                      const base = programmeBookingBuildAnswersFromInviteFields(fs);

                                      setAnswers(base);
                                      setV2AnswersFromRow(row);

                                      const mode = String(
                                        propz.get(data, ['selectionRules', 'mode'], 'SINGLE')
                                      ).toUpperCase();
                                      if (mode === 'PRIORITY') {
                                        const { initialPriority } = getPriorityDefaultsForRow(row);
                                        setDraftPriority(initialPriority);
                                      } else {
                                        setDraftPriority(null);
                                      }

                                      setIsModalOpen(true);
                                    }}
                                    title="Edit your saved answers"
                                  >
                                    Answered
                                  </span>
                                ) : null}

                                {String(propz.get(data, ['selectionRules', 'mode'], 'SINGLE')).toUpperCase() ===
                                'PRIORITY'
                                  ? (() => {
                                      const pr = propz.get(row, ['invite', 'priority'], undefined);
                                      const statusUpper = String(
                                        propz.get(row, ['invite', 'invitationStatus'], '') || ''
                                      ).toUpperCase();
                                      const isDraftLocal = isPendingSubmitStatus(statusUpper);
                                      const isAcceptedLocal = statusUpper === 'ACCEPTED';

                                      if (typeof pr !== 'number' && !isDraftLocal) return null;

                                      if (isDraftLocal) {
                                        return (
                                          <span
                                            role="button"
                                            className={`badge ${
                                              typeof pr === 'number' ? 'badge-info' : 'badge-light border'
                                            } mr-2 mb-2`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={e => {
                                              e.preventDefault();
                                              e.stopPropagation();

                                              const { initialPriority } = getPriorityDefaultsForRow(row);

                                              setSaveError(null);
                                              setPriorityRow(row);
                                              setPriorityValue(initialPriority);
                                              setIsPriorityModalOpen(true);
                                            }}
                                            title="Edit priority"
                                          >
                                            {typeof pr === 'number' ? `P${pr}` : 'Set priority'}
                                          </span>
                                        );
                                      }

                                      if (isAcceptedLocal && typeof pr === 'number') {
                                        return (
                                          <span className="badge badge-info mr-2 mb-2" title="Priority">
                                            P{pr}
                                          </span>
                                        );
                                      }

                                      return null;
                                    })()
                                  : null}
                              </div>
                            </div>

                            <div className="mt-auto">
                              {isAccepted ? (
                                isSubmittedLockedView ? (
                                  <div className="text-center">
                                    <div className="small text-success font-weight-bold">Submitted to school</div>
                                    <div className="small text-muted mt-1">
                                      This selection is locked while the school reviews allocations.
                                    </div>
                                  </div>
                                ) : (
                                  <div className="d-flex" style={{ gap: 10 }}>
                                    <button
                                      className="btn btn-outline-success flex-fill"
                                      disabled={!canChangeAccepted || isSaving}
                                      onClick={async () => {
                                        setAcceptedActionMode('CHANGE');
                                        setSaveError(null);
                                        setActiveRow(row);

                                        const fs = (propz.get(row, ['invite', 'fields'], []) || []) as any[];
                                        const base = programmeBookingBuildAnswersFromInviteFields(fs);
                                        const dayKey = getRowDayKey(row);

                                        setAnswers(prefillAnswersForClub(dayKey, fs, base));
                                        setV2AnswersFromRow(row);

                                        const mode = String(
                                          propz.get(data, ['selectionRules', 'mode'], 'SINGLE')
                                        ).toUpperCase();
                                        if (mode === 'PRIORITY') {
                                          const { initialPriority } = getPriorityDefaultsForRow(row);
                                          setDraftPriority(initialPriority);
                                        } else {
                                          setDraftPriority(null);
                                        }

                                        setIsModalOpen(true);
                                      }}
                                    >
                                      Change
                                    </button>

                                    <button
                                      className="btn btn-outline-danger flex-fill"
                                      disabled={!canChangeAccepted || isSaving}
                                      onClick={() => {
                                        setAcceptedActionMode('REMOVE');
                                        setSaveError(null);
                                        setRemoveAcceptedRow(row);
                                        setIsRemoveAcceptedModalOpen(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )
                              ) : (
                                <button
                                  className={`btn btn-block ${isDraft ? 'btn-outline-secondary' : 'btn-primary'}`}
                                  disabled={!canClickButton || isSaving}
                                  onClick={async () => {
                                    if (isDraft) {
                                      await removeDraft(row);
                                      return;
                                    }

                                    const mode = String(
                                      propz.get(data, ['selectionRules', 'mode'], 'SINGLE') || 'SINGLE'
                                    ).toUpperCase();
                                    const existingDraft =
                                      mode === 'SINGLE'
                                        ? programmeBookingFindExistingDraftInSameDay(active, clubId)
                                        : null;

                                    if (existingDraft) {
                                      setSaveError(null);
                                      setReplaceFromRow(existingDraft);
                                      setReplaceToRow(row);
                                      setIsReplaceModalOpen(true);
                                      return;
                                    }

                                    setSaveError(null);
                                    setActiveRow(row);

                                    const fs = (propz.get(row, ['invite', 'fields'], []) || []) as any[];
                                    const base = programmeBookingBuildInitialAnswersFromFields(fs);
                                    const dayKey = getRowDayKey(row);

                                    setAnswers(prefillAnswersForClub(dayKey, fs, base));
                                    setV2AnswersFromRow(row);

                                    if (mode === 'PRIORITY') {
                                      const { initialPriority } = getPriorityDefaultsForRow(row);
                                      setDraftPriority(initialPriority);
                                    } else {
                                      setDraftPriority(null);
                                    }

                                    setIsModalOpen(true);
                                  }}
                                >
                                  {isDraft
                                    ? 'Remove selection'
                                    : isBeforeOpen && preAccess === 'VIEW'
                                    ? 'Preview only'
                                    : 'Select club'}
                                </button>
                              )}

                              {canConfirm ? (
                                <div className="small text-muted text-center mt-2">
                                  Draft first, then confirm from the top panel.
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {isModalOpen && activeRow ? (
        <SimpleModal
          isOpen={isModalOpen}
          onCloseClick={() => {
            if (isSaving) return;
            setIsModalOpen(false);
            setActiveRow(null);
            clearModalAnswers();
            setSaveError(null);
            setDraftPriority(null);
          }}
          showFooter={false}
          title={
            isSubmittedLockedView &&
            String(propz.get(activeRow, ['invite', 'invitationStatus'], '') || '').toUpperCase() === 'ACCEPTED'
              ? 'View selection'
              : String(propz.get(activeRow, ['invite', 'invitationStatus'], '') || '').toUpperCase() === 'ACCEPTED'
              ? 'Update selection'
              : 'Select club'
          }
        >
          <div className="programmeBookingModal">
            {(() => {
              const clubName = String(propz.get(activeRow, ['club', 'name'], 'Club'));
              const isInvitesOnlyClub = !!propz.get(activeRow, ['invitesOnly'], false);
              const dayLabelLocal =
                String(
                  propz.get(
                    activeRow,
                    ['_dayLabel'],
                    propz.get(
                      activeRow,
                      ['dayLabel'],
                      propz.get(activeRow, ['_day'], propz.get(activeRow, ['day'], ''))
                    )
                  )
                ) || '';
              const timeLocal = programmeBookingFormatTimeRange(propz.get(activeRow, ['club', 'schedule'], {}));
              const datesLocal = programmeBookingFormatDateRange(propz.get(activeRow, ['club', 'schedule'], {}));
              const invStatusLocal = String(
                propz.get(activeRow, ['invite', 'invitationStatus'], '') || ''
              ).toUpperCase();
              const isAcceptedLocal = invStatusLocal === 'ACCEPTED';
              const isReadOnlyAnsweredModal = isSubmittedLockedView && isAcceptedLocal;

              return (
                <>
                  <div className="programmeBookingModalHero mb-4">
                    <div className="programmeBookingModalHeroTop">
                      <div>
                        <div className="programmeBookingModalEyebrow">
                          {isReadOnlyAnsweredModal
                            ? 'Viewing submitted selection'
                            : isAcceptedLocal
                            ? 'Updating selection'
                            : 'Club selection'}
                        </div>
                        <div className="programmeBookingModalClubName">{clubName}</div>
                      </div>

                      <div className="programmeBookingModalBadges">
                        {isInvitesOnlyClub ? <span className="badge badge-dark mr-2 mb-2">Invites only</span> : null}

                        {actorLabel ? <span className="badge badge-light border mr-2 mb-2">{actorLabel}</span> : null}

                        {dayLabelLocal ? (
                          <span className="badge badge-light border mr-2 mb-2">{dayLabelLocal}</span>
                        ) : null}
                      </div>
                    </div>

                    {timeLocal || datesLocal ? (
                      <div className="programmeBookingModalMeta mt-2">
                        {timeLocal ? <span>{timeLocal}</span> : null}
                        {datesLocal ? <span>{datesLocal}</span> : null}
                      </div>
                    ) : null}

                    {isInvitesOnlyClub ? (
                      <div className="programmeBookingModalInviteNote mt-3">
                        {actorLabel
                          ? `${actorLabel} has been specifically invited to this club.`
                          : actorFallbackLabel
                          ? `${actorFallbackLabel} has been specifically invited to this club.`
                          : 'You have been specifically invited to this club.'}
                      </div>
                    ) : null}
                  </div>

                  {saveError ? (
                    <div className="alert alert-danger" role="alert">
                      {saveError}
                    </div>
                  ) : null}

                  {isReadOnlyAnsweredModal ? (
                    <div className="alert alert-light border mb-3">
                      These answers have already been submitted to school and are now shown in read-only mode.
                    </div>
                  ) : null}

                  {String(propz.get(data, ['selectionRules', 'mode'], 'SINGLE')).toUpperCase() === 'PRIORITY' ? (
                    <div className="programmeBookingQuestionCard mb-3">
                      {(() => {
                        const { min, max, usedPriorities, nextAvailablePriority } = getPriorityDefaultsForRow(
                          activeRow
                        );

                        return (
                          <>
                            <label className="font-weight-bold mb-1">Priority</label>

                            <div className="d-flex align-items-center flex-wrap">
                              <select
                                className="form-control"
                                style={{ maxWidth: 220 }}
                                value={draftPriority === null ? '' : String(draftPriority)}
                                disabled={isReadOnlyAnsweredModal}
                                onChange={e => {
                                  const v = (e.target as HTMLSelectElement).value;
                                  setDraftPriority(v ? Number(v) : null);
                                }}
                              >
                                <option value="">Please select…</option>
                                {(() => {
                                  const out: number[] = [];
                                  for (let i = min; i <= max; i++) out.push(i);

                                  const currentPriority =
                                    draftPriority !== null && Number.isFinite(Number(draftPriority))
                                      ? Number(draftPriority)
                                      : null;
                                  const usedSet = new Set(usedPriorities);

                                  return out.map(n => {
                                    const isCurrent = currentPriority === n;
                                    const isUsedByAnother = usedSet.has(n) && !isCurrent;

                                    return (
                                      <option key={n} value={n} disabled={isUsedByAnother}>
                                        {isUsedByAnother ? `${n} (already used)` : n}
                                      </option>
                                    );
                                  });
                                })()}
                              </select>

                              <div className="small text-muted ml-3">1 = highest priority</div>
                            </div>

                            <div className="small text-muted mt-2">
                              {usedPriorities.length > 0
                                ? `Already used on this day: ${usedPriorities.map(n => `P${n}`).join(', ')}`
                                : 'No priorities used on this day yet.'}
                            </div>

                            {nextAvailablePriority !== null ? (
                              <div className="small text-info mt-1">
                                Suggested priority: <b>P{nextAvailablePriority}</b>
                              </div>
                            ) : (
                              <div className="small text-warning mt-1">
                                All priorities in this range are already used for this day.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : null}

                  {(() => {
                    const fields = (propz.get(activeRow, ['invite', 'fields'], []) || []) as any[];
                    const definition = getFormDefinition(activeRow);

                    if (definition) {
                      return (
                        <div className="mb-3">
                          <FormRendererV2
                            definition={definition}
                            answers={formAnswers}
                            errors={formErrors}
                            disabled={isReadOnlyAnsweredModal}
                            onChange={nextAnswers => {
                              setFormAnswers(nextAnswers);
                              setFormErrors({});
                              if (hasFormFlow(definition)) setIsFormFlowComplete(false);
                            }}
                            onSubmitRequested={() => setIsFormFlowComplete(true)}
                          />
                        </div>
                      );
                    }

                    if (!fields.length) {
                      return null;
                    }

                    return (
                      <div className="mb-3">
                        {fields.map(f => {
                          const key = programmeBookingFieldKey(f);
                          const heading = String(propz.get(f, ['heading'], 'Question'));
                          const t = programmeBookingFieldType(f);
                          const required = programmeBookingIsFieldRequired(f);
                          const v = answers[key];

                          return (
                            <div className="form-group" key={key}>
                              <label className="font-weight-bold">
                                {heading} {required ? <span className="text-danger">*</span> : null}
                              </label>

                              {t === 'BOOLEAN' ? (
                                <div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={key}
                                      checked={v === true}
                                      disabled={isReadOnlyAnsweredModal}
                                      onChange={() => setAnswers(a => ({ ...a, [key]: true }))}
                                    />
                                    <label className="form-check-label">Yes</label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={key}
                                      checked={v === false}
                                      disabled={isReadOnlyAnsweredModal}
                                      onChange={() => setAnswers(a => ({ ...a, [key]: false }))}
                                    />
                                    <label className="form-check-label">No</label>
                                  </div>
                                </div>
                              ) : t === 'ENUM' ? (
                                <select
                                  className="form-control"
                                  value={typeof v === 'undefined' ? '' : String(v)}
                                  disabled={isReadOnlyAnsweredModal}
                                  onChange={e => {
                                    const value = (e.target as HTMLSelectElement).value;
                                    setAnswers(a => ({ ...a, [key]: value }));
                                  }}
                                >
                                  <option value="">Please select…</option>
                                  {(propz.get(f, ['enumOptions'], []) || []).map((opt: any) => (
                                    <option key={String(opt)} value={String(opt)}>
                                      {String(opt)}
                                    </option>
                                  ))}
                                </select>
                              ) : t === 'NUMBER' ? (
                                <input
                                  className="form-control"
                                  type="number"
                                  value={typeof v === 'undefined' ? '' : String(v)}
                                  readOnly={isReadOnlyAnsweredModal}
                                  disabled={isReadOnlyAnsweredModal}
                                  onChange={e => {
                                    const value = (e.target as HTMLInputElement).value;
                                    setAnswers(a => ({ ...a, [key]: value }));
                                  }}
                                />
                              ) : (
                                <input
                                  className="form-control"
                                  type="text"
                                  value={typeof v === 'undefined' ? '' : String(v)}
                                  readOnly={isReadOnlyAnsweredModal}
                                  disabled={isReadOnlyAnsweredModal}
                                  onChange={e => {
                                    const value = (e.target as HTMLInputElement).value;
                                    setAnswers(a => ({ ...a, [key]: value }));
                                  }}
                                />
                              )}

                              {required ? <div className="small text-muted mt-1">Required</div> : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-outline-secondary mr-2"
                      disabled={isSaving}
                      onClick={() => {
                        setIsModalOpen(false);
                        setActiveRow(null);
                        clearModalAnswers();
                        setSaveError(null);
                        setDraftPriority(null);
                      }}
                    >
                      {isReadOnlyAnsweredModal ? 'Close' : 'Cancel'}
                    </button>

                    {!isReadOnlyAnsweredModal ? (
                      <button
                        className="btn btn-primary"
                        disabled={
                          isSaving ||
                          (!!getFormDefinition(activeRow) &&
                            hasFormFlow(getFormDefinition(activeRow) as FormDefinition) &&
                            !isFormFlowComplete)
                        }
                        onClick={async () => {
                          try {
                            if (!activeRow) return;

                            const fields = (propz.get(activeRow, ['invite', 'fields'], []) || []) as any[];
                            const definition = getFormDefinition(activeRow);
                            let answeredFields = programmeBookingBuildAnsweredFields(fields, answers);
                            let v2Submission: Pick<
                              ProgrammeBookingDraftPayload,
                              'formSchemaVersion' | 'formAnswers'
                            > = {};

                            if (definition) {
                              const errors = validateFormDefinition(
                                definition,
                                formAnswers,
                                getSubmissionElementIds(definition, formAnswers)
                              );
                              setFormErrors(errors);
                              const firstError = Object.values(errors)[0];
                              if (firstError) {
                                setSaveError(firstError);
                                return;
                              }

                              const answerList = answerMapToArray(formAnswers);
                              answeredFields = applyAnswersToLegacyFields(
                                formDefinitionToLegacyFields(definition),
                                definition,
                                answerList
                              );
                              v2Submission = {
                                formSchemaVersion: 2,
                                formAnswers: answerList
                              };
                            } else {
                              const errs = programmeBookingValidateAnswers(fields, answers);
                              if (errs.length) {
                                setSaveError(errs[0]);
                                return;
                              }
                            }

                            const mode = String(propz.get(data, ['selectionRules', 'mode'], 'SINGLE')).toUpperCase();

                            if (mode === 'PRIORITY') {
                              if (draftPriority === null || !Number.isFinite(Number(draftPriority))) {
                                setSaveError('Please select a priority for this club.');
                                return;
                              }

                              const dayKey = getRowDayKey(activeRow);
                              const dayRow = (days || []).find(d => String(propz.get(d, ['day'], '')) === dayKey);
                              const clubId = String(propz.get(activeRow, ['clubId'], ''));

                              const conflict = dayRow
                                ? programmeBookingFindPriorityConflictInDay(dayRow, clubId, Number(draftPriority))
                                : null;

                              if (conflict) {
                                const conflictName = String(propz.get(conflict, ['club', 'name'], 'another club'));
                                setSaveError(
                                  `Priority P${draftPriority} is already used for "${conflictName}" on this day. Please choose a different priority.`
                                );
                                return;
                              }
                            }

                            setSaveError(null);
                            setIsSaving(true);

                            const playerDetails = propz.get(activeRow, ['invite', 'playerDetails'], null);

                            const payload: ProgrammeBookingDraftPayload = {
                              playerDetails: {
                                userId: String(propz.get(playerDetails, ['userId'], '')),
                                permissionId: String(propz.get(playerDetails, ['permissionId'], ''))
                              },
                              fields: answeredFields,
                              ...v2Submission,
                              ...(mode === 'PRIORITY' && draftPriority !== null ? { priority: draftPriority } : {})
                            };

                            const invStatus = String(
                              propz.get(activeRow, ['invite', 'invitationStatus'], '') || ''
                            ).toUpperCase();
                            const isAcceptedInvite = invStatus === 'ACCEPTED';
                            const isDraftInvite = isPendingSubmitStatus(invStatus);
                            const isNewSelection = !isAcceptedInvite && !isDraftInvite;

                            if (isAcceptedInvite) {
                              const resp = await onUpdateAcceptedClub(activeRow, payload);
                              programmeBookingThrowIfOkError(resp);
                            } else {
                              const resp = await onDraftClub(activeRow, payload);
                              programmeBookingThrowIfOkError(resp);

                              if (isNewSelection) {
                                const dk = normalizeDayKey(getRowDayKey(activeRow));
                                const alreadyChosenAll = !!presets.chosenAll;
                                const alreadyChosenDay = !!(presets.chosenByDay && presets.chosenByDay[dk]);

                                if (!presets.dontAskAgain && !alreadyChosenAll && !alreadyChosenDay) {
                                  setPresetPromptPayload({ dayKey: dk, fields, answers });
                                  setIsPresetPromptOpen(true);
                                }
                              }
                            }

                            setIsModalOpen(false);
                            setActiveRow(null);
                            clearModalAnswers();
                            setDraftPriority(null);

                            await onReload();
                          } catch (e) {
                            const code = String(propz.get(e, ['response', 'data', 'code'], ''));
                            const apiMessage = String(
                              propz.get(
                                e,
                                ['response', 'data', 'error'],
                                propz.get(e, ['response', 'data', 'message'], '')
                              ) || ''
                            ).trim();

                            if (code === 'PRIORITY_ALREADY_USED' && apiMessage) {
                              setSaveError(apiMessage);
                              return;
                            }

                            setSaveError(programmeBookingFormatError(e, days));
                          } finally {
                            setIsSaving(false);
                          }
                        }}
                      >
                        {String(propz.get(activeRow, ['invite', 'invitationStatus'], '') || '').toUpperCase() ===
                        'ACCEPTED'
                          ? 'Save changes'
                          : 'Continue'}
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
        </SimpleModal>
      ) : null}

      {isReplaceModalOpen && replaceFromRow && replaceToRow ? (
        <SimpleModal
          isOpen={isReplaceModalOpen}
          title={`Replace ${replaceDayLabel} selection?`}
          onCloseClick={() => {
            if (isSaving) return;
            setIsReplaceModalOpen(false);
            setReplaceFromRow(null);
            setReplaceToRow(null);
            setSaveError(null);
          }}
          buttonText="Replace"
          onButtonClick={async () => {
            try {
              setSaveError(null);
              setIsSaving(true);

              const pd = propz.get(replaceFromRow, ['invite', 'playerDetails'], null);

              const payload: ProgrammeBookingConfirmPayload = {
                playerDetails: {
                  userId: String(propz.get(pd, ['userId'], '')),
                  permissionId: String(propz.get(pd, ['permissionId'], ''))
                }
              };

              await onUndraftClub(replaceFromRow, payload);

              setIsReplaceModalOpen(false);
              setReplaceFromRow(null);

              const nextRow = replaceToRow;
              setReplaceToRow(null);

              const fs = (propz.get(nextRow, ['invite', 'fields'], []) || []) as any[];
              const definition = getFormDefinition(nextRow);
              const dayKey = getRowDayKey(nextRow);
              const base = programmeBookingBuildInitialAnswersFromFields(fs);
              const prefilled = prefillAnswersForClub(dayKey, fs, base);

              const mode = String(propz.get(data, ['selectionRules', 'mode'], 'SINGLE')).toUpperCase();
              const priorityValueForNext: number | null =
                mode === 'PRIORITY' ? getPriorityDefaultsForRow(nextRow).initialPriority : null;

              if (!fs.length && !definition) {
                const playerDetails = propz.get(nextRow, ['invite', 'playerDetails'], null);

                const payload: ProgrammeBookingDraftPayload = {
                  playerDetails: {
                    userId: String(propz.get(playerDetails, ['userId'], '')),
                    permissionId: String(propz.get(playerDetails, ['permissionId'], ''))
                  },
                  fields: [],
                  ...(mode === 'PRIORITY' && priorityValueForNext !== null ? { priority: priorityValueForNext } : {})
                };

                const resp = await onDraftClub(nextRow, payload);
                programmeBookingThrowIfOkError(resp);

                setActiveRow(null);
                clearModalAnswers();
                setDraftPriority(null);

                await onReload();
                return;
              }

              setActiveRow(nextRow);
              setAnswers(prefilled);
              setV2AnswersFromRow(nextRow);
              setDraftPriority(priorityValueForNext);
              setIsModalOpen(true);
            } catch (e) {
              setSaveError(programmeBookingFormatError(e, days));
            } finally {
              setIsSaving(false);
            }
          }}
        >
          <div className="programmeBookingModal">
            {saveError ? (
              <div className="alert alert-danger" role="alert">
                {saveError}
              </div>
            ) : null}

            <div className="mb-3">
              <div className="d-flex align-items-start">
                <div
                  className="mr-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: 'rgba(0,123,255,0.12)',
                    color: '#007bff',
                    fontSize: 20,
                    flex: '0 0 auto'
                  }}
                  aria-hidden="true"
                >
                  ↺
                </div>

                <div>
                  <div className="h5 mb-1">Replace your selection for {replaceDayLabel}?</div>
                  <div className="text-muted">
                    You already have a <b>draft</b> selection for this day. Replacing will remove it and let you select
                    a new club.
                  </div>
                </div>
              </div>
            </div>

            <div
              className="p-3"
              style={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 12,
                background: '#fff'
              }}
            >
              <div className="d-flex flex-column flex-md-row align-items-stretch">
                <div className="flex-fill">
                  <div className="small text-muted mb-1">Current</div>
                  <div className="font-weight-bold">{String(propz.get(replaceFromRow, ['club', 'name'], 'Club'))}</div>
                  <div className="small text-muted mt-1">This draft will be removed.</div>
                </div>

                <div className="d-none d-md-flex align-items-center px-3 text-muted" aria-hidden="true">
                  <span style={{ fontSize: 18 }}>→</span>
                </div>

                <div className="flex-fill mt-3 mt-md-0">
                  <div className="small text-muted mb-1">New</div>
                  <div className="font-weight-bold">{String(propz.get(replaceToRow, ['club', 'name'], 'Club'))}</div>
                  <div className="small text-muted mt-1">You’ll answer questions next.</div>
                </div>
              </div>

              <div className="alert alert-warning py-2 mb-0 mt-3">
                <span className="font-weight-bold">Heads up:</span> replacing will remove the current draft selection
                for this day.
              </div>
            </div>

            <div className="small text-muted mt-2">You can always change your choice again while booking is open.</div>
          </div>
        </SimpleModal>
      ) : null}

      {isPriorityModalOpen && priorityRow ? (
        <SimpleModal
          isOpen={isPriorityModalOpen}
          title="Edit priority"
          onCloseClick={() => {
            if (isSaving) return;
            setIsPriorityModalOpen(false);
            setPriorityRow(null);
            setPriorityValue(null);
            setSaveError(null);
          }}
          showFooter={false}
        >
          <div className="programmeBookingModal">
            {saveError ? (
              <div className="alert alert-danger" role="alert">
                {saveError}
              </div>
            ) : null}

            <div className="mb-3 text-muted">
              Set the priority for <b>{String(propz.get(priorityRow, ['club', 'name'], 'Club'))}</b>.
            </div>

            <div className="form-group">
              <label className="font-weight-bold">Priority</label>
              {(() => {
                const { min, max, usedPriorities, nextAvailablePriority } = getPriorityDefaultsForRow(priorityRow);
                const opts: number[] = [];
                for (let i = min; i <= max; i += 1) opts.push(i);

                return (
                  <>
                    <select
                      className="form-control"
                      value={priorityValue === null ? '' : String(priorityValue)}
                      onChange={e => setPriorityValue(Number((e.target as HTMLSelectElement).value))}
                    >
                      {(() => {
                        const currentPriority =
                          priorityValue !== null && Number.isFinite(Number(priorityValue))
                            ? Number(priorityValue)
                            : null;
                        const usedSet = new Set(usedPriorities);

                        return opts.map(n => {
                          const isCurrent = currentPriority === n;
                          const isUsedByAnother = usedSet.has(n) && !isCurrent;

                          return (
                            <option key={n} value={n} disabled={isUsedByAnother}>
                              {isUsedByAnother ? `${n} (already used)` : n}
                            </option>
                          );
                        });
                      })()}
                    </select>

                    <div className="small text-muted mt-1">Lower number = higher priority.</div>

                    <div className="small text-muted mt-2">
                      {usedPriorities.length > 0
                        ? `Already used on this day: ${usedPriorities.map(n => `P${n}`).join(', ')}`
                        : 'No priorities used on this day yet.'}
                    </div>

                    {nextAvailablePriority !== null ? (
                      <div className="small text-info mt-1">
                        Suggested priority: <b>P{nextAvailablePriority}</b>
                      </div>
                    ) : (
                      <div className="small text-warning mt-1">
                        All priorities in this range are already used for this day.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-outline-secondary mr-2"
                disabled={isSaving}
                onClick={() => {
                  setIsPriorityModalOpen(false);
                  setPriorityRow(null);
                  setPriorityValue(null);
                  setSaveError(null);
                }}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                disabled={isSaving}
                onClick={async () => {
                  try {
                    if (!priorityRow) return;

                    if (priorityValue === null || !Number.isFinite(Number(priorityValue))) {
                      setSaveError('Please select a priority.');
                      return;
                    }

                    setSaveError(null);
                    setIsSaving(true);

                    const playerDetails = propz.get(priorityRow, ['invite', 'playerDetails'], null);
                    const fields = (propz.get(priorityRow, ['invite', 'fields'], []) || []) as any[];

                    const payload: ProgrammeBookingDraftPayload = {
                      playerDetails: {
                        userId: String(propz.get(playerDetails, ['userId'], '')),
                        permissionId: String(propz.get(playerDetails, ['permissionId'], ''))
                      },
                      fields,
                      priority: Number(priorityValue)
                    };

                    const clubId = String(propz.get(priorityRow, ['clubId'], ''));
                    const dayKey = getRowDayKey(priorityRow);
                    const dayRow = (days || []).find(d => String(propz.get(d, ['day'], '')) === String(dayKey));

                    const conflict = dayRow
                      ? programmeBookingFindPriorityConflictInDay(dayRow, clubId, Number(priorityValue))
                      : null;

                    if (conflict) {
                      const conflictName = String(propz.get(conflict, ['club', 'name'], 'another club'));
                      setSaveError(
                        `Priority P${priorityValue} is already used for "${conflictName}" on this day. Please choose a different priority.`
                      );
                      return;
                    }

                    const resp = await onDraftClub(priorityRow, payload);
                    programmeBookingThrowIfOkError(resp);

                    setIsPriorityModalOpen(false);
                    setPriorityRow(null);
                    setPriorityValue(null);
                    setSaveError(null);

                    await onReload();
                  } catch (e) {
                    setSaveError(programmeBookingFormatError(e, days));
                  } finally {
                    setIsSaving(false);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </SimpleModal>
      ) : null}

      {isConfirmModalOpen ? (
        <SimpleModal
          isOpen={isConfirmModalOpen}
          title="Review your selections"
          showFooter={false}
          onCloseClick={() => {
            if (isConfirming) return;
            setIsConfirmModalOpen(false);
            setConfirmError(null);
          }}
        >
          <div className="programmeBookingModal">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="small text-muted">Reviewing selections for</div>
              {actorLabel ? <span className="badge badge-primary">{actorLabel}</span> : null}
            </div>

            {confirmError ? (
              <div className="alert alert-danger" role="alert">
                {confirmError}
              </div>
            ) : null}

            <div className="alert alert-info py-2">
              <b>Sent to school</b> means you’ve submitted your choices so the school can review them.
              <div className="small text-muted mt-1">
                {actorLabel
                  ? 'It does not mean this child has a confirmed place yet.'
                  : 'It does not mean you have a confirmed place yet.'}
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="h5 mb-0">Not sent yet</div>
                <span className="badge badge-info">Pending changes: {pendingSubmitRowsCount}</span>
              </div>

              {pendingSubmitRowsCount === 0 ? (
                <div className="alert alert-light border mt-2 mb-0">No unsent changes to send.</div>
              ) : (
                <div className="mt-2">
                  {draftByDay.map(g => (
                    <div key={`draft-${g.day}`} className="mb-3">
                      <div
                        className="d-flex align-items-center justify-content-between mb-2 px-2 py-1"
                        style={{
                          position: 'relative',
                          borderRadius: 10,
                          background: 'rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          paddingLeft: 14
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 4,
                            borderRadius: 10,
                            background: '#007bff'
                          }}
                        />

                        <div className="font-weight-bold">{g.dayLabel}</div>
                        <span className="badge badge-light border">{g.rows.length}</span>
                      </div>

                      {g.rows.map((r: any) => {
                        const name = String(propz.get(r, ['club', 'name'], propz.get(r, ['clubId'], 'Club')));
                        const pr = propz.get(r, ['invite', 'priority'], undefined);

                        const pendingStatus = String(
                          propz.get(r, ['invite', 'invitationStatus'], '') || ''
                        ).toUpperCase();

                        const pendingLabel =
                          pendingStatus === 'PROGRAMME_DRAFT'
                            ? 'New selection'
                            : pendingStatus === 'PROGRAMME_DRAFT_UPDATE'
                            ? 'Updated selection'
                            : pendingStatus === 'PROGRAMME_DRAFT_REMOVE'
                            ? 'Removal'
                            : 'Pending change';

                        return (
                          <div
                            key={`${g.day}-${String(propz.get(r, ['clubId'], ''))}`}
                            className="p-2 mb-2 border rounded"
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                <div className="text-muted">{name}</div>
                                <div className="mt-1">
                                  <span className="badge badge-warning mr-2">{pendingLabel}</span>
                                  {renderReviewClubBadges(r)}
                                </div>
                              </div>

                              <div className="text-right">
                                {selectionMode === 'PRIORITY' && typeof pr === 'number' ? (
                                  <span className="badge badge-info">P{pr}</span>
                                ) : null}
                                <div className="small text-muted mt-1">
                                  {pendingStatus === 'PROGRAMME_DRAFT_REMOVE' ? 'Will be removed' : 'Will be sent'}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {pendingSubmitRowsCount > 0 ? (
                <div className="alert alert-warning py-2 mb-0">
                  <b>Important:</b> if you don’t confirm before the booking window closes, the school will not see draft
                  selections.
                </div>
              ) : null}
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="h5 mb-0">Already sent to school</div>
                <span className="badge badge-success">Sent: {acceptedRows.length}</span>
              </div>

              {acceptedRows.length === 0 ? (
                <div className="alert alert-light border mt-2 mb-0">Nothing has been sent yet.</div>
              ) : (
                <div className="mt-2">
                  {acceptedByDay.map(g => (
                    <div key={`acc-${g.day}`} className="mb-3">
                      <div
                        className="d-flex align-items-center justify-content-between mb-2 px-2 py-1"
                        style={{
                          position: 'relative',
                          borderRadius: 10,
                          background: 'rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          paddingLeft: 14
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 4,
                            borderRadius: 10,
                            background: '#007bff'
                          }}
                        />

                        <div className="font-weight-bold">{g.dayLabel}</div>
                        <span className="badge badge-light border">{g.rows.length}</span>
                      </div>

                      {g.rows.map((r: any) => {
                        const name = String(propz.get(r, ['club', 'name'], propz.get(r, ['clubId'], 'Club')));
                        const pr = propz.get(r, ['invite', 'priority'], undefined);

                        return (
                          <div
                            key={`acc-${g.day}-${String(propz.get(r, ['clubId'], ''))}`}
                            className="p-2 mb-2 border rounded"
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                <div className="text-muted">{name}</div>
                                {renderReviewClubBadges(r)}
                              </div>
                              <div className="text-right">
                                {selectionMode === 'PRIORITY' && typeof pr === 'number' ? (
                                  <span className="badge badge-info">P{pr}</span>
                                ) : null}
                                <div className="small text-muted mt-1">Sent</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div className="small text-muted">
                    {allowChangesAfterSubmit
                      ? 'You can still add more drafts and confirm again while the booking window is open.'
                      : 'These selections have already been sent to school and are now locked.'}
                  </div>
                </div>
              )}
            </div>

            {reviewWarnings.length > 0 ? (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="h5 mb-0">Please review</div>
                  <span className="badge badge-warning">Warnings: {reviewWarnings.length}</span>
                </div>

                <div className="mt-2">
                  {reviewWarnings.map((w, idx) => (
                    <div key={`${w.type}-${w.day}-${idx}`} className="alert alert-warning mb-2">
                      <div className="font-weight-bold mb-1">{w.dayLabel}</div>
                      <div>{w.message}</div>

                      {w.clubs && w.clubs.length > 0 ? (
                        <div className="mt-2">
                          {w.clubs.map((c: any, i: number) => {
                            const isInvitesOnly = !!propz.get(c, ['invitesOnly'], false);
                            const name = String(propz.get(c, ['club', 'name'], propz.get(c, ['clubId'], 'Club')));

                            return (
                              <div key={`${w.day}-club-${i}`} className="small">
                                {isInvitesOnly ? <span className="badge badge-dark mr-2">Invites only</span> : null}
                                {name}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-outline-secondary mr-2"
                disabled={isConfirming}
                onClick={() => {
                  setIsConfirmModalOpen(false);
                  setConfirmError(null);
                }}
              >
                Close
              </button>

              <button
                className="btn btn-success"
                disabled={isConfirming || pendingSubmitRowsCount === 0 || !canConfirm}
                onClick={async () => {
                  try {
                    setConfirmError(null);
                    setIsConfirming(true);

                    const pd = programmeBookingPickAnyPlayerDetails(data);
                    if (!pd) {
                      setConfirmError('Cannot confirm: player details are missing.');
                      return;
                    }

                    await onConfirmProgramme({ playerDetails: pd });

                    setIsConfirmModalOpen(false);
                    setIsSubmitStatusModalOpen(true);
                    await onReload();
                  } catch (e) {
                    setConfirmError(programmeBookingFormatError(e, days));
                  } finally {
                    setIsConfirming(false);
                  }
                }}
              >
                {isConfirming ? 'Sending…' : 'Confirm & send'}
              </button>
            </div>
          </div>
        </SimpleModal>
      ) : null}

      {isPresetPromptOpen && presetPromptPayload ? (
        <SimpleModal
          isOpen={true}
          title="Reuse your answers?"
          onCloseClick={() => {
            setIsPresetPromptOpen(false);
            setPresetPromptPayload(null);
          }}
          showFooter={false}
        >
          <div className="programmeBookingModal">
            <div className="text-muted mb-3">
              Some clubs share the same questions. Would you like to prefill your answers next time?
            </div>

            <label className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={!!presets.dontAskAgain}
                onChange={e => {
                  const checked = (e.target as HTMLInputElement).checked;
                  setPresets(p => ({ ...p, dontAskAgain: checked }));
                }}
              />
              <span className="small text-muted">Don’t ask again</span>
            </label>

            <div className="d-flex flex-wrap justify-content-end" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  const dk = normalizeDayKey(presetPromptPayload.dayKey);

                  setPresets(p => ({
                    ...p,
                    scope: 'NONE',
                    chosenByDay: p.dontAskAgain ? p.chosenByDay || {} : { ...(p.chosenByDay || {}), [dk]: true },
                    chosenAll: p.dontAskAgain ? true : p.chosenAll
                  }));

                  setIsPresetPromptOpen(false);
                  setPresetPromptPayload(null);
                }}
              >
                Fill manually for this day
              </button>

              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  const dk = normalizeDayKey(presetPromptPayload.dayKey);

                  applyAnswersToPresets('DAY', dk, presetPromptPayload.fields, presetPromptPayload.answers);

                  setPresets(p => ({
                    ...p,
                    chosenByDay: { ...(p.chosenByDay || {}), [dk]: true }
                  }));

                  setIsPresetPromptOpen(false);
                  setPresetPromptPayload(null);
                }}
              >
                Prefill for this day
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  applyAnswersToPresets(
                    'ALL',
                    presetPromptPayload.dayKey,
                    presetPromptPayload.fields,
                    presetPromptPayload.answers
                  );

                  setPresets(p => ({ ...p, chosenAll: true }));

                  setIsPresetPromptOpen(false);
                  setPresetPromptPayload(null);
                }}
              >
                Prefill for all clubs
              </button>
            </div>
          </div>
        </SimpleModal>
      ) : null}

      {isRemoveAcceptedModalOpen && removeAcceptedRow ? (
        <SimpleModal
          isOpen={true}
          title="Cancel this selection?"
          onCloseClick={() => {
            if (isSaving) return;
            setIsRemoveAcceptedModalOpen(false);
            setRemoveAcceptedRow(null);
            setSaveError(null);
          }}
          showFooter={false}
        >
          <div className="programmeBookingModal">
            {saveError ? (
              <div className="alert alert-danger" role="alert">
                {saveError}
              </div>
            ) : null}

            <div className="mb-3">
              <div className="h5 mb-2">Are you sure you want to cancel this booking?</div>

              <div className="text-muted">
                Your current selection for <b>{String(propz.get(removeAcceptedRow, ['club', 'name'], 'this club'))}</b>{' '}
                will be removed and returned to pending.
              </div>
            </div>

            <div className="alert alert-warning">
              If you change your mind, you can make a new selection or update it again until <b>{bookingCloseText}</b>.
              <div className="small mt-2">
                Please note that schools may review bookings in the order they are received, so cancelling and
                submitting again later could affect priority.
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-outline-secondary mr-2"
                disabled={isSaving}
                onClick={() => {
                  setIsRemoveAcceptedModalOpen(false);
                  setRemoveAcceptedRow(null);
                  setSaveError(null);
                }}
              >
                Keep selection
              </button>

              <button
                className="btn btn-danger"
                disabled={isSaving}
                onClick={async () => {
                  try {
                    await cancelAccepted(removeAcceptedRow);
                    setIsRemoveAcceptedModalOpen(false);
                    setRemoveAcceptedRow(null);
                    setSaveError(null);
                  } catch (e) {}
                }}
              >
                {isSaving ? 'Removing…' : 'Cancel selection'}
              </button>
            </div>
          </div>
        </SimpleModal>
      ) : null}

      {isLeaveWarningOpen ? (
        <SimpleModal
          isOpen={true}
          title="Leave this page?"
          onCloseClick={() => {
            setIsLeaveWarningOpen(false);
            setPendingLeaveAction(null);
          }}
          showFooter={false}
        >
          <div className="programmeBookingModal">
            <div className="alert alert-warning">
              <b>Your selections have not been sent to school yet.</b>
            </div>

            <div className="mb-3 text-muted">
              You currently have <b>{pendingSubmitRowsCount}</b> unsent change{pendingSubmitRowsCount === 1 ? '' : 's'}.
              They are saved, and you can still return and send them to school before <b>{bookingCloseText}</b>.
            </div>

            <div className="mb-3">
              Please don’t forget to click <b>Review &amp; send</b> when you are ready.
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-outline-secondary mr-2"
                onClick={() => {
                  setIsLeaveWarningOpen(false);
                  setPendingLeaveAction(null);
                }}
              >
                Stay here
              </button>

              <button
                className="btn btn-warning"
                onClick={() => {
                  const action = pendingLeaveAction;
                  setIsLeaveWarningOpen(false);
                  setPendingLeaveAction(null);
                  if (action) action();
                }}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </SimpleModal>
      ) : null}
    </div>
  );
};

